/**
 * Browser Performance API wrapper for measuring operation timing.
 * Only logs in development mode. Safe for jsdom/test environments.
 */

const isDev = process.env.NODE_ENV === "development";

const hasPerformanceAPI =
  typeof performance !== "undefined" &&
  typeof performance.mark === "function" &&
  typeof performance.measure === "function";

export const perfMark = (name: string): void => {
  if (hasPerformanceAPI) {
    performance.mark(name);
  }
};

export const perfMeasure = (name: string, startMark: string): number => {
  if (!hasPerformanceAPI) return 0;

  try {
    performance.measure(name, startMark);
    const entries = performance.getEntriesByName(name, "measure");
    const duration = entries.length ? entries[entries.length - 1].duration : 0;

    if (isDev && duration > 0) {
      console.log(`[PERF] ${name}: ${duration.toFixed(2)}ms`);
    }

    return duration;
  } catch {
    return 0;
  }
};

export const perfClear = (name: string): void => {
  if (hasPerformanceAPI) {
    performance.clearMarks?.(name);
    performance.clearMeasures?.(name);
  }
};

/**
 * Convenience wrapper to time a synchronous operation.
 */
export const timeOperation = <T>(name: string, operation: () => T): T => {
  perfMark(`${name}:start`);
  const result = operation();
  perfMeasure(name, `${name}:start`);
  perfClear(`${name}:start`);
  perfClear(name);
  return result;
};
